/**
 * Webtech HR Engine™ - Skills Dashboard
 * Tableau de bord de gestion des compétences
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Brain,
  Users,
  Target,
  AlertTriangle,
  TrendingUp,
  Award,
  Loader2,
  ChevronRight,
  BarChart3,
  Eye
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

export default function SkillsDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId;
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  useEffect(() => {
    if (companyId) loadData();
  }, [companyId, selectedDepartment]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ company_id: companyId });
      if (selectedDepartment !== 'all') {
        params.append('department_id', selectedDepartment);
      }
      
      const [dashboardRes, deptRes] = await Promise.all([
        api.get(`/skills/dashboard/team?${params}`),
        api.get('/departments')
      ]);
      
      setDashboardData(dashboardRes.data);
      setDepartments(deptRes.data.departments || deptRes.data || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard Compétences">
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  const summary = dashboardData?.summary || {};
  const skillMatrix = dashboardData?.skillMatrix || [];
  const employees = dashboardData?.employees || [];

  return (
    <DashboardLayout title="Dashboard Compétences">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/skills')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-webtech-blue" />
              Dashboard des compétences
            </h1>
            <p className="text-slate-500 mt-1">Vue d'ensemble des compétences de l'équipe</p>
          </div>
          
          <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Tous les départements" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les départements</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="h-6 w-6 mx-auto text-slate-400 mb-2" />
                <div className="text-2xl font-bold text-slate-900">{summary.totalEmployees || 0}</div>
                <p className="text-xs text-slate-500">Employés</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Award className="h-6 w-6 mx-auto text-green-500 mb-2" />
                <div className="text-2xl font-bold text-green-600">{summary.employeesEvaluated || 0}</div>
                <p className="text-xs text-slate-500">Évalués</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Brain className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                <div className="text-2xl font-bold text-blue-600">{summary.totalSkillsTracked || 0}</div>
                <p className="text-xs text-slate-500">Compétences</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="h-6 w-6 mx-auto text-yellow-500 mb-2" />
                <div className="text-2xl font-bold text-yellow-600">{summary.employeesWithGaps || 0}</div>
                <p className="text-xs text-slate-500">Avec écarts</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="h-6 w-6 mx-auto text-red-500 mb-2" />
                <div className="text-2xl font-bold text-red-600">{summary.criticalGapsTotal || 0}</div>
                <p className="text-xs text-slate-500">Écarts critiques</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Target className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                <div className="text-2xl font-bold text-purple-600">{summary.activeCampaigns || 0}</div>
                <p className="text-xs text-slate-500">Campagnes</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <TrendingUp className="h-6 w-6 mx-auto text-orange-500 mb-2" />
                <div className="text-2xl font-bold text-orange-600">{summary.activeDevelopmentPlans || 0}</div>
                <p className="text-xs text-slate-500">Plans actifs</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Skill Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Distribution des compétences</CardTitle>
              <CardDescription>Niveau moyen par compétence</CardDescription>
            </CardHeader>
            <CardContent>
              {skillMatrix.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Aucune donnée disponible
                </div>
              ) : (
                <div className="space-y-4">
                  {skillMatrix.slice(0, 8).map((skill, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-slate-700">{skill.skillName}</span>
                        <span className="text-slate-500">
                          {skill.averageLevel?.toFixed(1)} / 5 ({skill.totalEmployees} pers.)
                        </span>
                      </div>
                      <Progress value={(skill.averageLevel / 5) * 100} className="h-2" />
                    </div>
                  ))}
                  {skillMatrix.length > 8 && (
                    <p className="text-sm text-slate-500 text-center">
                      +{skillMatrix.length - 8} autres compétences
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Level Distribution for Top Skills */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Répartition par niveau</CardTitle>
              <CardDescription>Distribution des niveaux de compétence</CardDescription>
            </CardHeader>
            <CardContent>
              {skillMatrix.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Aucune donnée disponible
                </div>
              ) : (
                <div className="space-y-4">
                  {skillMatrix.slice(0, 5).map((skill, idx) => (
                    <div key={idx}>
                      <p className="text-sm font-medium text-slate-700 mb-2">{skill.skillName}</p>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(level => {
                          const count = skill.distribution?.[level] || 0;
                          const total = skill.totalEmployees || 1;
                          const percentage = (count / total) * 100;
                          return (
                            <div 
                              key={level}
                              className="flex-1 bg-slate-100 rounded-full h-6 relative overflow-hidden"
                              title={`Niveau ${level}: ${count} personnes`}
                            >
                              <div 
                                className={`absolute inset-0 ${
                                  level === 1 ? 'bg-slate-400' :
                                  level === 2 ? 'bg-blue-400' :
                                  level === 3 ? 'bg-green-400' :
                                  level === 4 ? 'bg-purple-400' :
                                  'bg-orange-400'
                                }`}
                                style={{ width: `${percentage}%` }}
                              />
                              <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                                {count > 0 && count}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between text-xs text-slate-400 mt-1">
                        <span>Débutant</span>
                        <span>Expert</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Employees Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Collaborateurs</CardTitle>
            <CardDescription>Liste des collaborateurs et leur profil de compétences</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Collaborateur</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead>Profil de poste</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map(emp => (
                  <TableRow key={emp.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-webtech-blue/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-webtech-blue">
                            {emp.firstName?.[0]}{emp.lastName?.[0]}
                          </span>
                        </div>
                        <span className="font-medium">{emp.firstName} {emp.lastName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500">{emp.position || '-'}</TableCell>
                    <TableCell>
                      {emp.jobProfileId ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Assigné
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-50 text-slate-500">
                          Non assigné
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/skills/employee/${emp.id}`)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Voir profil
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {employees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                      Aucun collaborateur
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
